import { area, bbox, center } from "@turf/turf";
import { useMemo } from "react";

import { useFrontendSetting } from "@nens/ranalib";
import { useMaptilerReverseGeosearch } from "@nens/react/hooks";

import { DrawGeometryFormData } from "../types/draw-geometry-form-data";
/**
 * Hook to get an appropriate place name for a geometry based on its size.
 * Larger geometries get less specific names (region), smaller ones get more specific (neighborhood).
 */
export const useGeometryPlaceName = (formData: DrawGeometryFormData | undefined) => {
  const { maptilerApiKey } = useFrontendSetting();

  // Check if coordinates are valid
  const coordinates = formData?.coordinates;
  const coordinatesAreValid =
    coordinates &&
    (typeof coordinates[0] === "number" ||
      (Array.isArray(coordinates[0]) &&
        (typeof coordinates[0][0] === "number" ||
          (Array.isArray(coordinates[0][0]) && typeof coordinates[0][0][0] === "number"))));

  const geocoderResponse = useMaptilerReverseGeosearch({
    maptilerApiKey: maptilerApiKey ?? "",
    coordinate: coordinatesAreValid
      ? (center(formData).geometry.coordinates as [number, number])
      : undefined,
    locale: "en",
    bbox: coordinatesAreValid ? (bbox(formData) as [number, number, number, number]) : undefined,
    enabled: !!(coordinatesAreValid && maptilerApiKey),
  });

  const feature = geocoderResponse.data?.features[0];

  return useMemo(() => {
    if (!feature || !coordinatesAreValid) {
      return null;
    }

    // Calculate area in square kilometers to determine appropriate place name level
    const areaInKm2 = area(formData) / 1_000_000;

    // Loop through context items to find the most accurate place name based on area size
    // Context items are ordered from most specific to least specific, so for example
    // [neighborhood, place, municipality, district, region]
    // Any of them can be missing, so we check for existence before returning the name
    const getContextName = (...prefix: string[]) => {
      const contextItem = feature.context?.find((c) => prefix.some((p) => c.id.startsWith(p)));
      return contextItem?.text ?? null;
    };

    if (areaInKm2 < 1) {
      const neighborhood = getContextName("neighborhood.");
      if (neighborhood) return neighborhood;
    }
    if (areaInKm2 < 10) {
      const place = getContextName("place.");
      if (place) return place;
    }
    if (areaInKm2 < 25) {
      const place = getContextName("municipality.");
      if (place) return place;
    }
    if (areaInKm2 < 1000) {
      const district = getContextName("district.", "county.");
      if (district) return district;
    }

    const region = getContextName("region.", "province.");
    if (region) return region;

    // Fallback to place name if no context match
    return feature.place_name?.split(",")[0];
  }, [feature, formData, coordinatesAreValid]);
};
